import React, { useState, useEffect } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  titleColor: '#fff',
  bodyColor: 'rgba(255,255,255,0.8)',
};

const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const InfrastructureCost = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fas fa-chart-bar' },
    { id: 'costs', label: 'Cost Analysis', icon: 'fas fa-dollar-sign' },
    { id: 'resources', label: 'Resources', icon: 'fas fa-server' },
    { id: 'backup', label: 'Backup & DR', icon: 'fas fa-shield-alt' },
  ];

  return (
    <div className="ic-container">
      <div className="ic-header">
        <div className="ic-header-left">
          <h4>Infrastructure & Costs</h4>
          <p className="ic-subtitle">Cloud spend, resource utilization, backups & disaster recovery</p>
        </div>
        <ExportButtons
          data={[
            { service: 'AWS EC2', monthly: '$12,400', change: '+5.2%', category: 'Compute' },
            { service: 'AWS S3', monthly: '$3,200', change: '-2.1%', category: 'Storage' },
            { service: 'CloudFront CDN', monthly: '$8,750', change: '+12.3%', category: 'CDN' },
            { service: 'RDS (PostgreSQL)', monthly: '$4,100', change: '+1.8%', category: 'Database' },
            { service: 'Lambda', monthly: '$890', change: '-8.4%', category: 'Compute' },
            { service: 'ElastiCache', monthly: '$1,600', change: '+3.1%', category: 'Cache' },
            { service: 'Total Monthly', monthly: '$30,940', change: '+4.7%', category: 'Total' },
          ]}
          headers={[
            { key: 'service', label: 'Service' }, { key: 'monthly', label: 'Monthly Cost' },
            { key: 'change', label: 'Change' }, { key: 'category', label: 'Category' },
          ]}
          title="Infrastructure & Costs Report"
          filename="infrastructure_costs"
        />
      </div>

      <div className="ic-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`ic-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            <i className={t.icon}></i> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'costs' && <CostAnalysisTab />}
      {activeTab === 'resources' && <ResourcesTab />}
      {activeTab === 'backup' && <BackupDRTab />}
    </div>
  );
};

const OverviewTab = () => {
  const kpis = [
    { label: 'Monthly Cloud Cost', value: '$42,800', change: '+2.4%', positive: true, icon: 'fas fa-cloud', color: '#f82581' },
    { label: 'CDN Egress', value: '$8.20/TB', change: '+1.2%', positive: true, icon: 'fas fa-network-wired', color: '#6366f1' },
    { label: 'Cost per 1K Streams', value: '$0.42', change: '-0.05%', positive: true, icon: 'fas fa-stream', color: '#10b981' },
    { label: 'DR Ready', value: 'Yes', change: '', positive: true, icon: 'fas fa-check-circle', color: '#10b981' },
  ];

  const monthlyCostData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Cloud Cost',
        data: [38, 39.5, 40.2, 41, 41.8, 42.8],
        backgroundColor: 'rgba(248,37,129,0.6)',
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.6,
      },
    ],
  };

  const costBreakdownData = {
    labels: ['EC2/Compute', 'CDN/CloudFront', 'S3 Storage', 'RDS/Database', 'Lambda', 'Other'],
    datasets: [{
      data: [35, 22, 15, 12, 8, 8],
      backgroundColor: ['#f82581', '#6366f1', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'],
      borderWidth: 0,
      cutout: '68%',
      spacing: 3,
    }],
  };

  const chartOpts = (cb) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: cb }, grid: gridStyle, border: noBorder },
    },
  });

  return (
    <div className="ic-section">
      <div className="ic-metrics-grid">
        {kpis.map((k, i) => (
          <div key={i} className="ic-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="ic-metric-top">
              <div className="ic-metric-icon" style={{ background: k.color + '15', color: k.color }}>
                <i className={k.icon}></i>
              </div>
              {k.change && <span className={`ic-metric-change ${k.positive ? 'up' : 'down'}`}>
                <i className={`fas fa-arrow-${k.positive ? 'up' : 'down'}`}></i> {k.change}
              </span>}
            </div>
            <div className="ic-metric-value">{k.value}</div>
            <div className="ic-metric-label">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="ic-row-2col">
        <div className="ic-chart-card">
          <div className="ic-chart-head">
            <h6>Monthly Cost Trend</h6>
            <span className="ic-badge-sm">With $45K Budget Line</span>
          </div>
          <div className="ic-chart-body" style={{ height: 240 }}>
            <Bar data={monthlyCostData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: tooltipStyle },
              scales: {
                x: { ticks: tickStyle, grid: noGrid, border: noBorder },
                y: { ticks: { ...tickStyle, callback: v => '$' + v + 'K' }, grid: gridStyle, border: noBorder },
              },
            }} />
          </div>
          <div className="ic-chart-footer-note">
            <i className="fas fa-info-circle"></i> Budget threshold: $45K · Current: $42.8K
          </div>
        </div>
        <div className="ic-chart-card">
          <div className="ic-chart-head">
            <h6>Cost Breakdown by Service</h6>
          </div>
          <div className="ic-chart-body ic-doughnut-wrap" style={{ height: 240 }}>
            <Doughnut data={costBreakdownData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.label}: ${ctx.parsed}%` } }
              }
            }} />
          </div>
          <div className="ic-breakdown-labels">
            {['EC2/Compute', 'CDN/CloudFront', 'S3 Storage', 'RDS/Database', 'Lambda', 'Other'].map((s, i) => (
              <span key={i}>
                <span className="ic-dot" style={{ background: ['#f82581', '#6366f1', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'][i] }}></span>
                {s} {costBreakdownData.datasets[0].data[i]}%
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CostAnalysisTab = () => {
  const costTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Compute',
        data: [14.2, 14.8, 15.4, 16.1, 16.8, 17.5],
        borderColor: '#f82581',
        backgroundColor: 'rgba(248,37,129,0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'Storage',
        data: [5.7, 6.2, 6.8, 7.4, 8.0, 8.6],
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.05)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'CDN',
        data: [7.2, 7.5, 7.9, 8.3, 8.7, 9.1],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.05)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
    ],
  };

  const cdnTable = [
    { region: 'us-east-1', bandwidth: 145.2, requests: 1850, cost: '$1,162.40', efficiency: '92%', perGb: '$8.01' },
    { region: 'eu-west-1', bandwidth: 92.8, requests: 1240, cost: '$742.40', efficiency: '89%', perGb: '$7.99' },
    { region: 'ap-southeast-1', bandwidth: 78.5, requests: 980, cost: '$628.40', efficiency: '91%', perGb: '$8.00' },
    { region: 'ap-northeast-1', bandwidth: 54.3, requests: 720, cost: '$434.40', efficiency: '88%', perGb: '$8.00' },
    { region: 'sa-east-1', bandwidth: 38.6, requests: 520, cost: '$308.80', efficiency: '90%', perGb: '$8.00' },
    { region: 'ca-central-1', bandwidth: 31.2, requests: 410, cost: '$249.60', efficiency: '87%', perGb: '$8.00' },
  ];

  const costComparisonData = [
    { month: 'January', compute: 14.2, storage: 5.7, cdn: 7.2, database: 5.1, lambda: 3.4, total: 35.6, change: '0.0%' },
    { month: 'February', compute: 14.8, storage: 6.2, cdn: 7.5, database: 5.3, lambda: 3.7, total: 37.5, change: '+5.3%' },
    { month: 'March', compute: 15.4, storage: 6.8, cdn: 7.9, database: 5.5, lambda: 4.0, total: 39.6, change: '+5.6%' },
    { month: 'April', compute: 16.1, storage: 7.4, cdn: 8.3, database: 5.7, lambda: 4.1, total: 41.6, change: '+5.0%' },
    { month: 'May', compute: 16.8, storage: 8.0, cdn: 8.7, database: 5.9, lambda: 4.2, total: 43.6, change: '+4.8%' },
    { month: 'June', compute: 17.5, storage: 8.6, cdn: 9.1, database: 6.1, lambda: 4.4, total: 45.7, change: '+4.8%' },
  ];

  const chartOpts = (cb) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: cb }, grid: gridStyle, border: noBorder },
    },
  });

  return (
    <div className="ic-section">
      <div className="ic-chart-card">
        <div className="ic-chart-head">
          <h6>Cost Trend by Service</h6>
          <div className="ic-chart-legend-inline">
            <span><span className="ic-dot" style={{ background: '#f82581' }}></span> Compute</span>
            <span><span className="ic-dot" style={{ background: '#6366f1' }}></span> Storage</span>
            <span><span className="ic-dot" style={{ background: '#10b981' }}></span> CDN</span>
          </div>
        </div>
        <div className="ic-chart-body" style={{ height: 280 }}>
          <Line data={costTrendData} options={chartOpts(v => '$' + v + 'K')} />
        </div>
      </div>

      <div className="ic-chart-card ic-table-card">
        <div className="ic-chart-head">
          <h6>CDN Cost Analysis by Region</h6>
        </div>
        <div className="ic-table-wrap">
          <table className="ic-data-table">
            <thead>
              <tr>
                <th>Region</th>
                <th>Bandwidth (TB)</th>
                <th>Requests (M)</th>
                <th>Egress Cost</th>
                <th>Cache Efficiency</th>
                <th>Cost per GB</th>
              </tr>
            </thead>
            <tbody>
              {cdnTable.map((row, i) => (
                <tr key={i}>
                  <td className="ic-region">{row.region}</td>
                  <td>{row.bandwidth}</td>
                  <td>{row.requests}</td>
                  <td className="ic-cost">{row.cost}</td>
                  <td><span className="ic-efficiency">{row.efficiency}</span></td>
                  <td>{row.perGb}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ic-chart-card ic-table-card">
        <div className="ic-chart-head">
          <h6>Infrastructure Cost Comparison - 6 Months</h6>
        </div>
        <div className="ic-table-wrap">
          <table className="ic-data-table ic-comparison-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Compute</th>
                <th>Storage</th>
                <th>CDN</th>
                <th>Database</th>
                <th>Lambda</th>
                <th>Total</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {costComparisonData.map((row, i) => (
                <tr key={i}>
                  <td className="ic-month"><strong>{row.month}</strong></td>
                  <td>${row.compute}K</td>
                  <td>${row.storage}K</td>
                  <td>${row.cdn}K</td>
                  <td>${row.database}K</td>
                  <td>${row.lambda}K</td>
                  <td className="ic-total"><strong>${row.total}K</strong></td>
                  <td className={`ic-change ${row.change.includes('+') ? 'increase' : 'stable'}`}>{row.change}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ResourcesTab = () => {
  const resourceUtilizationData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: 'CPU %',
        data: [35, 32, 28, 25, 22, 25, 35, 48, 62, 75, 82, 85, 88, 86, 84, 82, 75, 68, 62, 58, 52, 45, 40, 38],
        borderColor: '#f82581',
        backgroundColor: 'rgba(248,37,129,0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'Memory %',
        data: [42, 40, 38, 36, 34, 38, 45, 55, 68, 78, 85, 88, 90, 88, 86, 84, 80, 75, 70, 65, 58, 50, 45, 43],
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'Disk I/O %',
        data: [28, 26, 24, 22, 20, 23, 32, 42, 55, 65, 72, 75, 78, 76, 74, 72, 65, 58, 50, 45, 38, 32, 28, 26],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
    ],
  };

  const chartOpts = (cb) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: { ...tickStyle, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: cb }, grid: gridStyle, border: noBorder },
    },
  });

  const utilMetrics = [
    { label: 'Peak CPU', value: '90%', icon: 'fas fa-microchip', color: '#f82581' },
    { label: 'Avg Memory', value: '64%', icon: 'fas fa-memory', color: '#6366f1' },
    { label: 'Disk I/O Peak', value: '78%', icon: 'fas fa-hdd', color: '#10b981' },
    { label: 'Network Util', value: '71%', icon: 'fas fa-network-wired', color: '#f59e0b' },
  ];

  return (
    <div className="ic-section">
      <div className="ic-metrics-grid ic-metrics-4col">
        {utilMetrics.map((m, i) => (
          <div key={i} className="ic-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="ic-metric-top">
              <div className="ic-metric-icon" style={{ background: m.color + '15', color: m.color }}>
                <i className={m.icon}></i>
              </div>
            </div>
            <div className="ic-metric-value">{m.value}</div>
            <div className="ic-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="ic-chart-card">
        <div className="ic-chart-head">
          <h6>Resource Utilization Over Time (24 Hours)</h6>
          <div className="ic-chart-legend-inline">
            <span><span className="ic-dot" style={{ background: '#f82581' }}></span> CPU %</span>
            <span><span className="ic-dot" style={{ background: '#6366f1' }}></span> Memory %</span>
            <span><span className="ic-dot" style={{ background: '#10b981' }}></span> Disk I/O %</span>
          </div>
        </div>
        <div className="ic-chart-body" style={{ height: 280 }}>
          <Line data={resourceUtilizationData} options={chartOpts(v => v + '%')} />
        </div>
        <div className="ic-chart-footer-note">
          <i className="fas fa-info-circle"></i> Peak utilization occurs during 10:00-20:00 · Baseline: 22-35%
        </div>
      </div>
    </div>
  );
};

const BackupDRTab = () => {
  const backupStatus = [
    { label: 'Last Full Backup', value: '2h ago', status: 'success', icon: 'fas fa-check' },
    { label: 'Last Incremental', value: '15min ago', status: 'success', icon: 'fas fa-check' },
    { label: 'Backup Size', value: '890GB', status: 'info', icon: 'fas fa-database' },
    { label: 'Retention', value: '30 days', status: 'info', icon: 'fas fa-calendar' },
    { label: 'RTO', value: '4 hours', status: 'info', icon: 'fas fa-clock' },
    { label: 'RPO', value: '15 minutes', status: 'info', icon: 'fas fa-sync' },
  ];

  const drDetails = [
    { label: 'Primary Region', value: 'us-east-1', substatus: 'Active', color: '#10b981' },
    { label: 'DR Region', value: 'us-west-2', substatus: 'Standby', color: '#f59e0b' },
    { label: 'Last DR Test', value: '2 weeks ago', substatus: 'Passed', color: '#10b981' },
    { label: 'Failover Time', value: 'Target: 4h', substatus: 'Achieved: 3h 22m', color: '#10b981' },
    { label: 'Data Sync Status', value: 'Real-time', substatus: '12ms lag', color: '#10b981' },
    { label: 'Multi-Region', value: 'Available', substatus: 'Not active', color: '#f59e0b' },
  ];

  const getStatusBg = (status) => {
    const colors = {
      success: 'rgba(16,185,129,0.15)',
      warning: 'rgba(245,158,11,0.15)',
      info: 'rgba(99,102,241,0.15)',
    };
    return colors[status] || colors.info;
  };

  const getStatusColor = (status) => {
    const colors = {
      success: '#10b981',
      warning: '#f59e0b',
      info: '#6366f1',
    };
    return colors[status] || colors.info;
  };

  return (
    <div className="ic-section">
      <div className="ic-chart-card">
        <div className="ic-chart-head">
          <h6>Backup & Recovery Status</h6>
        </div>
        <div className="ic-backup-grid">
          {backupStatus.map((b, i) => (
            <div key={i} className="ic-backup-item" style={{ background: getStatusBg(b.status) }}>
              <div className="ic-backup-top">
                <i className={b.icon} style={{ color: getStatusColor(b.status) }}></i>
                <span className="ic-backup-label">{b.label}</span>
              </div>
              <div className="ic-backup-value">{b.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="ic-chart-card">
        <div className="ic-chart-head">
          <h6>Disaster Recovery Readiness</h6>
        </div>
        <div className="ic-dr-grid">
          {drDetails.map((d, i) => (
            <div key={i} className="ic-dr-item">
              <div className="ic-dr-label">{d.label}</div>
              <div className="ic-dr-value" style={{ color: d.color }}>{d.value}</div>
              <div className="ic-dr-substatus">{d.substatus}</div>
            </div>
          ))}
        </div>
        <div className="ic-dr-summary">
          <div className="ic-dr-check">
            <i className="fas fa-shield-alt"></i>
            <span>Full disaster recovery infrastructure ready. RTO/RPO targets being met.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfrastructureCost;
